// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rapport's "Til bygningsaffald.dk" as data (BEK 496 §§ 10–11; prototype
 * `submission`): the two parts the case is submitted in, each a list of rows
 * that say what goes in, whether it is there, and where it is fixed.
 *
 *  - Del 1 · pkt. 1–8, with the application for the demolition permit: the
 *    resource screening, the bygherre's approval of it, Sagsdata, the
 *    ressourcekortlægning as PDF, the photo documentation and the
 *    miljøkortlægning (made outside ReUseX, attached as a PDF);
 *  - Del 2 · pkt. 9–22, before the demolition: every type approved, the
 *    sample answers, EAK and tons, the pkt. 10 fractions and the report PDF.
 *
 * Layered on the "Før indberetning" checklist (`buildChecklist`): its six
 * rows are reused with their figures and are the only ones that gate
 * Indberet (`gates`); the rest only inform. Nothing here is stored — ReUseX
 * keeps no submission status — so every row is computed from the case.
 *
 * Pure: no React, no DOM.
 */

import type { ReportPdfVersion, Resource, ResourceKey, Sample, SurveyFractions, SurveyPhotos, SurveyType } from '../api/types';
import { footStatus } from '../indberetning/model';
import { formatTonnes, type Tone } from '../kortlaegning/vocab';
import { isComplete, UNKNOWN_STATUS, versionDate, versionStatus } from '../rapport/model';
import {
  danishDate,
  lacksEakOrTons,
  reportedTypes,
  rowById,
  screenedKey,
  typeScreened,
  type CheckId,
  type CheckState,
  type Checklist,
  type ProjectRecord,
} from './checklist';
import type { StepTarget } from './overblik';
import { PDF_PAGE } from './pages';
import { isInstanceBacked, isManualType } from './provenance';
import { daysNote, daysUntil, deadline, PLAN_LEAD_DAYS } from './sagsdata';
import { INDBERETNING_PAGE } from './selection';

/**
 * Where a row leads: a selection, or a place in Rapport's own right tile.
 * `blocking` is "Det, der blokerer", which now lives in Gennemgang's left
 * tile: the host switches mode.
 */
export type SubmissionTarget =
  | { kind: 'sel'; sel: StepTarget }
  | { kind: 'generate' }
  | { kind: 'versions' }
  | { kind: 'blocking' };

/** `external`: made outside ReUseX (never counted as missing). */
export type SubmissionState = CheckState | 'external';

export type SubmissionRowId = CheckId | 'kortlPdf' | 'foto' | 'miljoe' | 'pkt10' | 'rapportPdf';

export interface SubmissionPill {
  tone?: Tone;
  /** A neutral outlined label (`Pill variant="outline"`). */
  outline?: boolean;
  text: string;
}

export interface SubmissionRow {
  id: SubmissionRowId;
  part: 1 | 2;
  title: string;
  detail: string;
  state: SubmissionState;
  pill: SubmissionPill;
  /** One of the checklist rows the Indberet button is gated on. */
  gates: boolean;
  target: SubmissionTarget;
}

export interface SubmissionPart {
  part: 1 | 2;
  /** "Del 1 · pkt. 1–8". */
  title: string;
  /** When it is submitted. */
  sub: string;
  /** Del 2's deadline is near (`warn`) or past (`crit`). */
  subTone?: 'warn' | 'crit';
  rows: SubmissionRow[];
  /** Rows not ok, external ones left out (unknown counts as missing). */
  missing: number;
}

export interface Submission {
  parts: [SubmissionPart, SubmissionPart];
  /** Del 1 until its rows are done, then Del 2. */
  next: { part: 1 | 2; missing: number };
}

export interface SubmissionInput {
  types: readonly SurveyType[];
  samples: readonly Sample[];
  /** `GET /survey/photos`; null while loading, or when the read failed (`photosFailed`). */
  photos: SurveyPhotos | null;
  /** The case is loaded but the photos are not: the read failed (or the server has no such route). */
  photosFailed?: boolean;
  fractions: SurveyFractions | null;
  /** Null while loading. */
  versions: readonly ReportPdfVersion[] | null;
  project: ProjectRecord | null;
  resourceKeys: readonly ResourceKey[];
  resources: readonly Resource[];
  now?: Date;
}

export const DEL1_SUB = 'Med ansøgningen om nedrivningstilladelse';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const STATE_PILL: Record<CheckState, SubmissionPill> = {
  ok: { tone: 'good', text: 'Udfyldt' },
  no: { tone: 'warn', text: 'Mangler' },
  unknown: { tone: 'wait', text: 'Ukendt' },
};

const LOADING_PILL: SubmissionPill = { tone: 'wait', text: 'Indlæser' };

const SAGSDATA: SubmissionTarget = { kind: 'sel', sel: { mode: 'overblik', patch: { edit: 'sag' } } };

/** A type in Ressourcer, or Ressourcer itself. */
function ressourcer(typeId: number | undefined): SubmissionTarget {
  return { kind: 'sel', sel: typeId === undefined ? { mode: 'ressourcer' } : { mode: 'ressourcer', patch: { typeId } } };
}

/** The newest version (by number, whatever order the list is in). */
function latestOf(versions: readonly ReportPdfVersion[]): ReportPdfVersion | null {
  return versions.length === 0 ? null : versions.reduce((a, b) => (b.version > a.version ? b : a));
}

function versionLine(v: ReportPdfVersion): string {
  return `Seneste version v${v.version} · ${versionDate(v.created_at)}`;
}

function checkRow(
  list: Checklist,
  id: CheckId,
  part: 1 | 2,
  title: string,
  target: SubmissionTarget,
  partial = false,
): SubmissionRow {
  const row = rowById(list, id);
  const pill = row.state === 'no' && partial ? { tone: 'warn' as const, text: 'Delvist' } : STATE_PILL[row.state];
  return { id, part, title, detail: row.detail, state: row.state, pill, gates: true, target };
}

/**
 * Photo documentation comes from the scan: every scan-backed type wants a
 * photo. Types made by hand cannot have one from the scan, so they are named
 * in the subline but never missing.
 */
function fotoRow(types: readonly SurveyType[], photos: SurveyPhotos | null, failed: boolean): SubmissionRow {
  const base = { id: 'foto' as const, part: 1 as const, title: 'Fotodokumentation', gates: false };
  if (photos === null) {
    const detail = failed ? 'Fotos kunne ikke hentes' : 'Fotos indlæses…';
    return { ...base, detail, state: 'unknown', pill: failed ? STATE_PILL.unknown : LOADING_PILL, target: ressourcer(undefined) };
  }
  if (types.length === 0) {
    return { ...base, detail: 'Ingen typer i kortlægningen endnu', state: 'no', pill: STATE_PILL.no, target: ressourcer(undefined) };
  }
  const scan = types.filter(isInstanceBacked);
  const has = (t: SurveyType) => t.parts.some((p) => (photos.parts[p.code]?.count ?? 0) > 0);
  const without = scan.filter((t) => !has(t));
  const manual = types.filter(isManualType).length;
  const manualNote = manual > 0 ? ` · ${manual} oprettet manuelt uden fotos` : '';
  const counted =
    scan.length === 0 ? 'Ingen typer fra scanningen' : `${scan.length - without.length} af ${plural(scan.length, 'type', 'typer')} har fotos fra scanningen`;
  const state: CheckState = without.length === 0 ? 'ok' : 'no';
  const pill = state === 'ok' ? STATE_PILL.ok : without.length < scan.length ? { tone: 'warn' as const, text: 'Delvist' } : STATE_PILL.no;
  return { ...base, detail: `${counted}${manualNote}`, state, pill, target: ressourcer(without[0]?.id) };
}

function miljoeRow(samples: readonly Sample[]): SubmissionRow {
  const open = samples.filter((s) => s.stage !== 'svar').length;
  return {
    id: 'miljoe',
    part: 1,
    title: 'Miljøkortlægning',
    detail: `Laves uden for ReUseX · ${plural(samples.length, 'prøve', 'prøver')}${open > 0 ? `, ${open} uden svar` : ''} i Miljø & prøver`,
    state: 'external',
    pill: { outline: true, text: 'Vedhæftes som PDF' },
    gates: false,
    target: { kind: 'sel', sel: { mode: 'miljoe' } },
  };
}

function pkt10Row(fractions: SurveyFractions | null): SubmissionRow {
  const base = {
    id: 'pkt10' as const,
    part: 2 as const,
    title: 'Pkt. 10 · affaldstyper og fraktioner',
    gates: false,
    target: { kind: 'sel' as const, sel: { mode: 'rapport' as const, patch: { page: INDBERETNING_PAGE } } },
  };
  if (!fractions) return { ...base, detail: 'Fraktionerne er ikke indlæst', state: 'unknown', pill: STATE_PILL.unknown };
  const n = fractions.fractions.length;
  const foot = footStatus(fractions);
  return {
    ...base,
    detail: `${plural(n, 'fraktion', 'fraktioner')} · ${formatTonnes(fractions.total_t)} godkendt`,
    state: foot.tone === 'good' ? 'ok' : 'no',
    pill: foot,
  };
}

/**
 * The stored report as a part submits it — Del 1's ressourcekortlægning and
 * Del 2's whole plan are the same PDF, so both rows read it the same way: ok
 * only when the newest version is Komplet; a kladde leads to what blocks it
 * or, once nothing does, to generating a new version.
 */
function reportRow(
  base: Pick<SubmissionRow, 'id' | 'part' | 'title' | 'gates'>,
  versions: readonly ReportPdfVersion[] | null,
  fractions: SurveyFractions | null,
  done: SubmissionTarget,
): SubmissionRow {
  if (versions === null) return { ...base, detail: 'Indlæser…', state: 'unknown', pill: LOADING_PILL, target: { kind: 'versions' } };
  // What blocks now (the live fractions), not what blocked at generation.
  const blockers = fractions ? fractions.blocking_types : null;
  const blocks = blockers !== null && blockers > 0 ? ` · ${plural(blockers, 'type blokerer', 'typer blokerer')}` : '';
  const latest = latestOf(versions);
  if (!latest) {
    return {
      ...base,
      detail: `Ikke genereret${blocks}`,
      state: 'no',
      pill: { tone: 'warn', text: 'Ikke genereret' },
      target: { kind: 'generate' },
    };
  }
  const status = versionStatus(latest);
  if (status === null) {
    return {
      ...base,
      detail: `${versionLine(latest)}${blocks}`,
      state: 'unknown',
      pill: { tone: 'wait', text: UNKNOWN_STATUS.text },
      target: done,
    };
  }
  if (isComplete(latest)) {
    return { ...base, detail: `${versionLine(latest)}${blocks}`, state: 'ok', pill: { tone: status.tone, text: status.text }, target: done };
  }
  // A kladde: what still blocks it, or — once nothing does — a new version would be complete.
  const clear = blockers === 0;
  return {
    ...base,
    detail: `${versionLine(latest)}${clear ? ' · intet blokerer nu — generér en ny version' : blocks}`,
    state: 'no',
    // `versionStatus`'s Kladde, in the tone the tile heading gives it too.
    pill: { tone: status.tone, text: status.text },
    target: clear ? { kind: 'generate' } : { kind: 'blocking' },
  };
}

function partOf(part: 1 | 2, title: string, sub: Deadline, rows: SubmissionRow[]): SubmissionPart {
  const missing = rows.filter((r) => r.state !== 'ok' && r.state !== 'external').length;
  const out: SubmissionPart = { part, title, sub: sub.text, rows, missing };
  if (sub.tone) out.subTone = sub.tone;
  return out;
}

export interface Deadline {
  text: string;
  tone?: 'warn' | 'crit';
}

/**
 * Del 2's deadline, toned as it nears (`daysNote`). The app plans with
 * `PLAN_LEAD_DAYS` (14) before the demolition — the prototype says "3 uger";
 * which one BEK 496 means for Del 2 must be verified against the order
 * before it is relied on.
 */
export function del2Deadline(project: ProjectRecord | null, now: Date = new Date()): Deadline {
  const at = deadline(project?.demolition_start ?? null);
  if (!at) return { text: `Senest ${PLAN_LEAD_DAYS} dage før nedrivningen` };
  const days = daysUntil(at, now);
  const n = days === null ? null : daysNote(days);
  if (!n) return { text: `Senest ${danishDate(at)}` };
  return n.tone ? { text: `Senest ${danishDate(at)} · ${n.note}`, tone: n.tone } : { text: `Senest ${danishDate(at)} · ${n.note}` };
}

/** Both parts and which one is next. */
export function buildSubmission(list: Checklist, input: SubmissionInput): Submission {
  const types = reportedTypes(input.types);
  const key = screenedKey(input.resourceKeys);
  const screened = key ? types.filter((t) => typeScreened(t.id, key.id, input.resources)) : [];
  const unscreened = key ? types.find((t) => !screened.includes(t)) : undefined;
  const openSample = input.samples.find((s) => s.stage !== 'svar');

  const pdf: SubmissionTarget = { kind: 'sel', sel: { mode: 'rapport', patch: { page: PDF_PAGE } } };

  const del1 = partOf(1, 'Del 1 · pkt. 1–8', { text: DEL1_SUB }, [
    checkRow(list, 'screened', 1, 'Ressourcescreening', ressourcer(unscreened?.id), screened.length > 0),
    checkRow(list, 'screeningApproved', 1, 'Screening godkendt af bygherre', SAGSDATA),
    checkRow(list, 'sagsdata', 1, 'Sagsdata komplette', SAGSDATA),
    reportRow({ id: 'kortlPdf', part: 1, title: 'Ressourcekortlægning som PDF', gates: false }, input.versions, input.fractions, {
      kind: 'versions',
    }),
    fotoRow(types, input.photos, input.photosFailed ?? false),
    miljoeRow(input.samples),
  ]);
  const del2 = partOf(2, 'Del 2 · pkt. 9–22', del2Deadline(input.project, input.now), [
    checkRow(list, 'approved', 2, 'Alle typer godkendt', { kind: 'sel', sel: { mode: 'gennemgang' } }),
    checkRow(
      list,
      'samples',
      2,
      'Prøvesvar modtaget',
      { kind: 'sel', sel: openSample ? { mode: 'miljoe', patch: { sampleId: openSample.id } } : { mode: 'miljoe' } },
    ),
    checkRow(list, 'eakTons', 2, 'EAK og tons på alle typer', ressourcer(types.find(lacksEakOrTons)?.id)),
    pkt10Row(input.fractions),
    reportRow({ id: 'rapportPdf', part: 2, title: 'Rapport som PDF', gates: false }, input.versions, input.fractions, pdf),
  ]);
  const next = del1.missing > 0 ? del1 : del2;
  return { parts: [del1, del2], next: { part: next.part, missing: next.missing } };
}

/** "1 punkt mangler" / "3 punkter mangler": the count both Rapport and Overblik's Næste skridt say. */
export function pointsMissingText(n: number): string {
  return plural(n, 'punkt mangler', 'punkter mangler');
}

/** "Del 1 · 3 punkter mangler" / "Del 2 · klar". */
export function nextSubmissionText(next: Submission['next']): string {
  if (next.missing === 0) return `Del ${next.part} · klar`;
  return `Del ${next.part} · ${pointsMissingText(next.missing)}`;
}
